import {
  BrowserRouter,
  Navigate,
  Outlet,
  Route,
  Routes,
  useLocation,
  useNavigate,
} from "react-router-dom";
import type { DriveStep } from "driver.js";
import dayjs from "dayjs";
import { useEffect, useMemo, useState } from "react";

import Authenticate from "./auth/Authenticate";
import Login from "./auth/LoginContainer";
import OnboardingContainer from "./public/onboarding/OnboardingContainer";

import BillingContainer from "./app/billing/BillingContainer";
import CustomersContainer from "./app/customers/CustomersContainer";
import InovicesContainer from "./app/invoices/InovicesContainer";
import PayoutsContainer from "./app/payouts/PayoutsContainer";
import PayoutBatchDetailContainer from "./app/payouts/detail/PayoutBatchDetailContainer";
import SettlementsContainer from "./app/settlements/SettlementsContainer";
import SettlementDetailContainer from "./app/settlements/detail/SettlementDetailContainer";
import SubscriptionContainer from "./app/subscriptions/SubscriptionContainer";
import SummaryContainer from "./app/summary/SummaryContainer";
import TutorialsContainer from "./app/tutorials/TutorialsContainer";
import WhatsAppContainer from "./app/whatsapp/WhatsAppContainer";
import ProtectContainer from "../protect/ProtectContainer";
import InductionContainer from "../protect/induction/InductionContainer";
import ContextRoute from "./ContextRoute";
import OrgAnalyticsContainer from "./app/org/OrgAnalyticsContainer";
import OrgBillingContainer from "./app/org/OrgBillingContainer";
import OrgMerchantsContainer from "./app/org/OrgMerchantsContainer";
import MerchantRequestContainer from "./app/org/merchantRequest/MerchantRequestContainer";
import { ProtectTRPCProvider } from "../protect/ProtectTRPCProvider";

import { useStytch, useStytchSession } from "@stytch/react";
import { useQuery } from "@tanstack/react-query";
import { MerchantProvider } from "../contexts/MerchantContext";
import { SideMenuProvider } from "../contexts/SideMenuContext";
import { TablesStateProvider } from "../contexts/TablesStateContext";
import { useTRPC } from "../trpc/client";
import { TRPCProvider } from "../trpc/client/TRPCProvider";

import { Alert, Box, Button, Text, VStack } from "@chakra-ui/react";
import RatingModal from "../components/RatingModal";
import TotpSetupModal, {
  TotpSetupProvider,
} from "../components/TotpSetupModal";
import SideMenu from "../components/SideMenu";
import WhatsappPopUpModal from "../components/WhatsappPopUpModal";
import PayoutsPopUpModal from "../components/PayoutsPopUpModal";
import { Toaster } from "../components/ui/toaster";
import { ROUTES } from "../config/routes";
import { orgNav } from "../config/navigation";
import { useApiVersionCheck } from "../hooks/useApiVersionCheck";
import { useFetchUser } from "../hooks/useFetchUser";
import { useTour } from "../hooks/useTour";
import { useOnboardingStatus } from "../hooks/useOnboardingStatus";
import OnboardingHome from "./app/onboarding/OnboardingHome";
import { useLocalStorage } from "usehooks-ts";
import { PAGE_TOURS, PAGE_TOUR_BY_PATH, WELCOME_TOUR } from "../config/tours";
import { useMerchant } from "../hooks/useMerchant";
import { usePortalContext } from "../hooks/usePortalContext";
import { useIsOrgAdmin } from "../hooks/useIsOrgAdmin";
import {
  isContextResolvable,
  resolveDefaultContext,
} from "../helpers/portalContext";
import { UserIdentification } from "./auth/UserIdentification";

export default function RouterContainer(): JSX.Element {
  return (
    <BrowserRouter>
      <Routes>
        {/* Public routes */}
        <Route path={ROUTES.AUTH.LOGIN} element={<Login />} />
        <Route path={ROUTES.AUTH.AUTHENTICATE} element={<Authenticate />} />
        <Route path={ROUTES.PUBLIC.ONBOARD} element={<OnboardingContainer />} />

        {/* Protected dashboard routes */}
        <Route element={<ProtectedRoutesLayout />}>
          <Route
            path={ROUTES.DASHBOARD.HOME}
            element={
              <ContextRoute
                merchant={<SummaryContainer />}
                org={<OrgAnalyticsContainer />}
              />
            }
          />
          <Route
            path={ROUTES.DASHBOARD.CUSTOMERS}
            element={<CustomersContainer />}
          />
          <Route
            path={ROUTES.DASHBOARD.SUBSCRIPTIONS}
            element={<SubscriptionContainer />}
          />
          <Route
            path={ROUTES.DASHBOARD.BILLING}
            element={
              <ContextRoute
                merchant={<BillingContainer />}
                org={<OrgBillingContainer />}
              />
            }
          />
          {/* Org-only screens: in merchant context there is no organization
              to create a comercio in, so the route bounces home. */}
          <Route
            path={ROUTES.DASHBOARD.MERCHANTS}
            element={
              <ContextRoute
                merchant={<Navigate to={ROUTES.DASHBOARD.HOME} replace />}
                org={<OrgMerchantsContainer />}
              />
            }
          />
          <Route
            path={ROUTES.DASHBOARD.MERCHANT_REQUEST}
            element={
              <ContextRoute
                merchant={<Navigate to={ROUTES.DASHBOARD.HOME} replace />}
                org={<MerchantRequestContainer />}
              />
            }
          />
          <Route
            path={ROUTES.DASHBOARD.MERCHANT_REQUEST_DETAIL}
            element={
              <ContextRoute
                merchant={<Navigate to={ROUTES.DASHBOARD.HOME} replace />}
                org={<MerchantRequestContainer />}
              />
            }
          />
          <Route
            path={ROUTES.DASHBOARD.INVOICES}
            element={<InovicesContainer />}
          />
          <Route
            path={ROUTES.DASHBOARD.SETTLEMENTS}
            element={<SettlementsContainer />}
          />
          <Route
            path={ROUTES.DASHBOARD.SETTLEMENT_DETAIL}
            element={<SettlementDetailContainer />}
          />
          <Route
            path={ROUTES.DASHBOARD.WHATSAPP}
            element={<WhatsAppContainer />}
          />
          <Route
            path={ROUTES.DASHBOARD.PAYOUTS}
            element={<PayoutsContainer />}
          />
          <Route
            path={ROUTES.DASHBOARD.PAYOUTS_BATCH}
            element={<PayoutBatchDetailContainer />}
          />
          <Route path={ROUTES.DASHBOARD.WELCOME} element={<OnboardingHome />} />
          <Route
            path={ROUTES.DASHBOARD.TUTORIALS}
            element={<TutorialsContainer />}
          />
          <Route
            path={ROUTES.DASHBOARD.PROTECT}
            element={<ProtectContainer />}
          />
          <Route
            path={ROUTES.DASHBOARD.PROTECT_INDUCTION}
            element={<InductionContainer />}
          />
        </Route>

        <Route path="*" element={<Navigate to={ROUTES.DASHBOARD.HOME} />} />
      </Routes>
    </BrowserRouter>
  );
}

function ApiVersionCheck(): null {
  useApiVersionCheck();
  return null;
}

const NO_STEPS: DriveStep[] = [];

const DashboardContent = (): JSX.Element => {
  const { merchant } = useMerchant();
  const { context } = usePortalContext();
  const isOrgAdmin = useIsOrgAdmin();
  const { user } = useFetchUser();
  const [totpSetupDismissed, setTotpSetupDismissed] = useState(false);
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const ready = !!merchant && !!user;

  // The welcome page opens once per session for a merchant with at most two
  // recaudos, and once ever for a recent user whose merchant already has them
  // (an ERP that synced before the first login). The dismissal there ends
  // both; the page stays reachable by URL afterwards.
  const status = useOnboardingStatus();
  const [dismissed] = useLocalStorage(
    `palomma:onboarding-home-dismissed:${merchant?.id ?? ""}`,
    false
  );
  const [seen] = useLocalStorage(
    `palomma:onboarding-home-seen:${merchant?.id ?? ""}`,
    false
  );
  const [welcomedId, setWelcomedId] = useState<string>();
  const isMerchant = context?.type === "merchant" && !!merchant;
  const isWelcome = pathname === ROUTES.DASHBOARD.WELCOME;
  const recentUser =
    !!user?.createdAt &&
    user.createdAt > dayjs().subtract(2, "month").toISOString();
  const candidate = isMerchant && !dismissed && welcomedId !== merchant?.id;
  const shouldWelcome =
    candidate &&
    !!status.data &&
    (status.data.invoiceCount <= 2 || (!seen && recentUser));
  const welcomePending =
    candidate &&
    (status.isPending || (!seen && !user) || (shouldWelcome && !isWelcome));
  useEffect(() => {
    if (!shouldWelcome || !merchant) return;
    setWelcomedId(merchant.id);
    if (!isWelcome) navigate(ROUTES.DASHBOARD.WELCOME, { replace: true });
  }, [shouldWelcome, isWelcome, merchant, navigate]);
  const shellReady = ready && !isWelcome && !welcomePending;

  // Org context reaches only its own screens, plus Tutoriales from the bottom
  // section. This covers a context switch that lands on a route the new nav
  // lacks, and equally a reload or a deep link into a merchant screen while in
  // org context. Merchant context is unconstrained, as it is today.
  useEffect(() => {
    if (context?.type !== "org") return;
    const nav = orgNav({ isOrgAdmin });
    const allowed = [
      ...nav.map((item) => item.path),
      ROUTES.DASHBOARD.TUTORIALS,
    ];
    if (allowed.includes(pathname)) return;
    // The request form is reachable but is not a nav item, so it is allowed
    // explicitly rather than by hiding an entry in the org nav.
    if (isOrgAdmin && pathname.startsWith(ROUTES.DASHBOARD.MERCHANT_REQUEST))
      return;
    navigate(nav[0].path, { replace: true });
  }, [context?.type, isOrgAdmin, pathname, navigate]);

  const pageTour = PAGE_TOUR_BY_PATH[pathname] ?? "";
  useTour("welcome", WELCOME_TOUR, shellReady);
  useTour(pageTour, PAGE_TOURS[pageTour] ?? NO_STEPS, shellReady);

  return (
    <TotpSetupProvider open={() => setTotpSetupDismissed(false)}>
      <ApiVersionCheck />
      <UserIdentification />
      {welcomePending ? (
        <LoadingScreen />
      ) : isWelcome ? (
        <Outlet />
      ) : (
        <>
          <Box display="flex">
            <SideMenu />
            <Box
              width="100%"
              height="100vh"
              display="flex"
              overflow="hidden"
              flexDirection="column"
              bg="#f8fafc"
            >
              <AlertBanner />
              <Outlet />
            </Box>
          </Box>
          <RatingModal />
          <WhatsappPopUpModal />
          <PayoutsPopUpModal />
        </>
      )}
      <TotpSetupModal
        isOpen={!!user?.showTotpSecretConfiguration && !totpSetupDismissed}
        onClose={() => setTotpSetupDismissed(true)}
      />
      <Toaster />
    </TotpSetupProvider>
  );
};

const LoadingScreen = (): JSX.Element => (
  <Box
    minH="100vh"
    display="flex"
    alignItems="center"
    justifyContent="center"
    bg="#f8fafc"
  >
    <Text fontSize="sm" color="gray.600">
      Cargando...
    </Text>
  </Box>
);

const AccessGate = ({
  children,
}: {
  children: React.ReactNode;
}): JSX.Element | null => {
  const trpc = useTRPC();
  const stytch = useStytch();
  const navigate = useNavigate();
  const { context, setContext } = usePortalContext();
  const { data, isError, error, isPending, refetch, isRefetching } = useQuery({
    ...trpc.merchants.list.queryOptions(undefined),
    staleTime: 60_000,
  });

  const {
    data: orgs,
    isPending: isOrgsPending,
    isError: isOrgsError,
  } = useQuery({
    ...trpc.organizations.list.queryOptions(undefined),
    staleTime: 60_000,
  });

  const scope = useMemo(
    () => ({
      merchantIds: (data ?? []).map((m) => m.id),
      orgIds: (orgs ?? []).map((o) => o.id),
    }),
    [data, orgs]
  );

  const resolved = useMemo(
    () => resolveDefaultContext(context, scope),
    [context, scope]
  );

  useEffect(() => {
    if (!resolved) return;
    if (context?.type === resolved.type && context?.id === resolved.id) return;
    setContext(resolved);
  }, [resolved, context, setContext]);

  if (isPending || isOrgsPending) return <LoadingScreen />;

  // An organization that exists but holds no buildings yet is a real state, and
  // a valid context to land in — not an access failure.
  const hasNoAccess = data?.length === 0 && orgs?.length === 0;

  if (isError || isOrgsError || hasNoAccess) {
    const isAuthError =
      (error &&
        "data" in error &&
        (error.data?.code === "UNAUTHORIZED" ||
          error.data?.code === "FORBIDDEN")) ||
      hasNoAccess;

    return (
      <Box
        minH="100vh"
        display="flex"
        alignItems="center"
        justifyContent="center"
        px={4}
        bg="#f8fafc"
      >
        <VStack gap={4} textAlign="center" maxW="md">
          <Text fontSize="lg" fontWeight="medium">
            {isAuthError
              ? "No pudimos confirmar tu acceso al portal de comercios."
              : "No pudimos cargar el portal."}
          </Text>
          <Text fontSize="sm" color="gray.600">
            {isAuthError
              ? "Necesitas tener al menos un comercio asignado. Intenta de nuevo, o cierra sesión si el problema persiste."
              : "Ocurrió un error de conexión. Intenta de nuevo."}
          </Text>
          <Box display="flex" gap={2}>
            <Button
              variant="outline"
              disabled={isRefetching}
              onClick={() => void refetch()}
            >
              Reintentar
            </Button>
            <Button
              variant="ghost"
              onClick={() =>
                void stytch.session.revoke().finally(() => {
                  navigate(ROUTES.AUTH.LOGIN, { replace: true });
                })
              }
            >
              Cerrar sesión
            </Button>
          </Box>
        </VStack>
      </Box>
    );
  }

  if (!isContextResolvable(context, scope)) return <LoadingScreen />;

  return <>{children}</>;
};

const ProtectedRoutesLayout = (): JSX.Element => {
  const { session } = useStytchSession();
  if (!session) return <Navigate to={ROUTES.AUTH.LOGIN} />;

  return (
    <MerchantProvider>
      <TablesStateProvider>
        <SideMenuProvider>
          <TRPCProvider>
            <ProtectTRPCProvider>
              <AccessGate>
                <DashboardContent />
              </AccessGate>
            </ProtectTRPCProvider>
          </TRPCProvider>
        </SideMenuProvider>
      </TablesStateProvider>
    </MerchantProvider>
  );
};

const AlertBanner = (): JSX.Element => {
  const { merchant } = useMerchant();

  if (merchant && !merchant?.active)
    return (
      <Alert.Root status="error" width="100%" minHeight={"46.9px"} rounded={0}>
        <Alert.Indicator />
        <Alert.Description>
          Este comercio está inactivo. Ciertas funcionalidades no estarán
          disponibles. Por favor, contactanos para reactivarlo.
        </Alert.Description>
      </Alert.Root>
    );

  if (!merchant?.merchantPortalBanner) return <></>;
  const banner = merchant.merchantPortalBanner;
  return (
    <Alert.Root
      status={banner.type || "warning"}
      width="100%"
      minHeight={"46.9px"}
      cursor={banner.link ? "pointer" : "default"}
      onClick={() => {
        if (banner.link) window.open(banner.link, "_blank");
      }}
    >
      <Alert.Indicator />
      <Alert.Title>{banner.title}</Alert.Title>
      <Alert.Description>{banner.text}</Alert.Description>
    </Alert.Root>
  );
};
